import useQuery from 'app/hooks/use-query'
import useRequester from 'app/hooks/use-requester'
import { rateContentMutation } from 'app/mutations/content/rate'
import { favouriteMutation } from 'app/mutations/content/toggle-favourite'
import {
  createContentPreviewQuery,
  createContentQuery,
  mapToPreviewContent,
  mapToContent,
} from 'app/queries/content/content'
import { useStores } from 'app/store'
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'

const standardQuery = createContentQuery()
const previewQuery = createContentPreviewQuery()
const favourite = favouriteMutation()
const rate = rateContentMutation()

interface ContentOptions {
  id?: string
  slug: string
  preview?: boolean
  token?: string
}

interface ContentHookStateParams {
  id?: string
  slug: string
}

export default function useContent(options: ContentOptions) {
  const { lastCollectionStore } = useStores()
  const queryClient = useQueryClient()
  const [mutating, setMutating] = useState(false)
  const { request } = useRequester()

  const [params, setParams] = useState<ContentHookStateParams>({
    id: options.id,
    slug: options.slug,
  })

  useEffect(() => {
    setParams({ slug: options.slug, id: options.id })
  }, [options.slug])

  const variables = useMemo(() => {
    const vars: any = { slug: params?.slug, id: params?.id }

    if (
      !lastCollectionStore ||
      !lastCollectionStore?.collectionId ||
      lastCollectionStore?.isPrimaryCollection
    ) {
      return vars
    }

    const { collectionId, collectionType } = lastCollectionStore

    if (collectionType === 'Collection') {
      vars.collection = collectionId
    } else if (collectionType === 'Tag') {
      vars.tag = collectionId
    } else if (collectionType === 'MemberTier') {
      vars.memberTier = collectionId
    }

    return vars
  }, [params.slug, lastCollectionStore])

  const query = useMemo(() => {
    if (options.preview) {
      return previewQuery
    }

    return standardQuery
  }, [options.preview])

  const { loading, data, error, fetchData } = useQuery(
    query,
    variables,
    options.preview ? mapToPreviewContent : mapToContent,
    {
      refetchOnMount: 'always',
      staleTime: Infinity,
      jwtToken: options.token,
    }
  )

  const toggleFavourite = async () => {
    setMutating(true)
    const result = await request(favourite, {
      id: data?.id,
    })
    await fetchData()
    setMutating(false)
    queryClient.invalidateQueries('favorites')
    return result
  }

  const rateContent = async (rating: number) => {
    const result = await request(rate, {
      id: data?.id,
      rating,
    })
    await fetchData()
    return result
  }

  useEffect(() => {
    if (lastCollectionStore && !lastCollectionStore.collectionId && data) {
      lastCollectionStore.setIsPrimaryCollection(true)
    }
  }, [data, lastCollectionStore])

  return {
    loading,
    data,
    error,
    fetchData,
    setParams,
    collectionName: lastCollectionStore?.collectionName,
    collectionId: lastCollectionStore?.collectionId,
    mutating,
    toggleFavourite,
    rateContent,
  }
}
